/***************************************************************************************************
 * Load `$localize` onto the global scope - used if i18n tags appear in Angular templates.
 */
import '@angular/localize/init';
/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/docs/ts/latest/guide/browser-support.html
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */

/**
 * Legacy TV / pre-ES6 WebKit support (LG NetCast drive-through displays).
 *
 * The differentially-loaded es5 bundle references a global `Proxy` (used by
 * Angular's runtime on these engines). LG NetCast (AppleWebKit/537.31, no
 * native Proxy) throws a ReferenceError before Angular can bootstrap, so the
 * confirmation screen never renders while modern browsers are unaffected.
 *
 * This installs a minimal constructor-emulating Proxy shim ONLY when the
 * platform has no native Proxy. Evergreen browsers keep their native Proxy
 * untouched. It intentionally does NOT touch `Symbol` (mutating core-js@2's
 * Symbol post-install corrupts its Set/Map — see the es5 _toPrimitive
 * post-build patch in scripts/patch-es5-toprimitive.js for the companion fix).
 */
(function () {
  try {
    if (typeof (window as any).Proxy === 'undefined') {
      (window as any).Proxy = function (target: any) {
        if (typeof target === 'function') {
          const shim: any = function (this: any) {
            if (this instanceof shim) {
              const obj = Object.create(target.prototype);
              const ret = target.apply(obj, arguments as any);
              return (typeof ret === 'object' && ret) ? ret : obj;
            }
            return target.apply(this, arguments as any);
          };
          shim.prototype = target.prototype;
          return shim;
        }
        return target;
      };
    }
  } catch (e) { /* no-op: native Proxy present or window unavailable */ }
})();

/**
 * ES2015 (ES6) runtime polyfills.
 *
 * Required by older smart-TV / kiosk browsers that run the customer-facing
 * screens — notably LG webOS / NetCast TVs whose built-in browser is an old
 * Chromium (roughly 38-53). Those engines load the legacy nomodule/es5 bundle,
 * which Angular downlevels for *syntax* only; it does NOT inject these runtime
 * polyfills. Without them the app throws an uncaught error during bootstrap
 * (missing Object.assign / Array.from / Symbol / Promise / Map / Set / etc.)
 * and the screen stays blank. These shims are feature-detected: on modern
 * browsers that already have the natives they are a no-op.
 */
import 'core-js/es6/symbol';
import 'core-js/es6/object';
import 'core-js/es6/function';
import 'core-js/es6/parse-int';
import 'core-js/es6/parse-float';
import 'core-js/es6/number';
import 'core-js/es6/math';
import 'core-js/es6/string';
import 'core-js/es6/date';
import 'core-js/es6/array';
import 'core-js/es6/regexp';
import 'core-js/es6/map';
import 'core-js/es6/weak-map';
import 'core-js/es6/set';

/**
 * The base Reflect API. Chromium added Reflect in v49, so webOS engines below
 * that (Chromium 38-48) need this before reflect-metadata (es7/reflect) below.
 */
import 'core-js/es6/reflect';


/** Evergreen browsers require these. **/
// Used for reflect-metadata in JIT. If you use AOT (and only Angular decorators), you can remove.
import 'core-js/es7/reflect';

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * user can disable parts of macroTask/DomEvents patch by setting following flags
 */

// (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
// (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
// (window as any).__zone_symbol__BLACK_LISTED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
(window as any).global = window;
/*
* in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
* with the following flag, it will bypass `zone.js` patch for IE/Edge
*/
// (window as any).__Zone_enable_cross_context_check = true;

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 *
 * IMPORTANT — Zone.js is NOT imported here. It is loaded as a standalone,
 * pre-built ES5 script via angular.json `scripts[]` (node_modules/zone.js/
 * dist/zone.js), which Angular concatenates VERBATIM (no TypeScript/Babel
 * downlevel, no webpack module wrapper) and injects after polyfills-es5.js and
 * before main-es5.js.
 *
 * Why: on LG NetCast / SmartTV 6.0 (AppleWebKit 537.31, no native Symbol), when
 * `import 'zone.js'` lived in this polyfills bundle it was Babel-es5-downleveled
 * and run in the core-js@2 object-Symbol environment. That pipeline (the same
 * class of failure as the `_toPrimitive` throw patched in scripts/patch-es5-
 * toprimitive.js) prevented Zone from assigning the global `Zone`, so main-es5
 * failed bootstrap with "In this configuration Angular requires Zone.js" and the
 * drive-through screen never rendered. Loading the hand-written ES5 UMD as a raw
 * script removes the entire transpile pipeline from Zone's path — Zone assigns
 * the global on every engine. Modern browsers are unaffected (ES5 Zone runs
 * everywhere; differential loading of the app bundles is preserved).
 *
 * DO NOT re-add `import 'zone.js'` here — Zone would then load twice and throw
 * "Zone already loaded".
 */

/**
 * web-animations-js — polyfills Element.animate() for old smart-TV engines. Kept
 * last. Zone.js is already loaded (via scripts[]) before this bundle even runs,
 * so a throw here can no longer break Angular bootstrap.
 */
import 'web-animations-js/web-animations.min';



/***************************************************************************************************
 * APPLICATION IMPORTS
 */
